import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Flex, Text, Image, Spinner, Input, Badge } from '@chakra-ui/react';
import { Search, UserCog, Users } from 'lucide-react';
import { useOrganizationAcceptedMembers } from '@/hooks/useOrganizationAcceptedMembers';
import type { OrganizationAcceptedMember } from '@/hooks/useOrganizationAcceptedMembers';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';

/** ~4–5 rows of ~72px each before scrolling (shared with pending requests list). */
export const MEMBERS_LIST_MAX_HEIGHT = '320px';

function displayName(m: { firstName: string | null; lastName: string | null }): string {
  const n = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
  return n || 'Volunteer';
}

type Props = {
  organizationUserId: string;
  /** Increment to refetch the members list (e.g. after manage modal saves). */
  refreshSignal?: number;
  /** While opening manage modal for this membership row (network fetch). */
  loadingMembershipId?: string | null;
  onManageMember: (member: OrganizationAcceptedMember) => void | Promise<void>;
};

function filterMembersBySearch(
  list: OrganizationAcceptedMember[],
  query: string
): OrganizationAcceptedMember[] {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter((m) => {
    const name = displayName(m).toLowerCase();
    const first = (m.firstName ?? '').toLowerCase();
    const last = (m.lastName ?? '').toLowerCase();
    return name.includes(q) || first.includes(q) || last.includes(q);
  });
}

export function OrganizationMembersSection({
  organizationUserId,
  refreshSignal = 0,
  loadingMembershipId = null,
  onManageMember,
}: Props) {
  const [search, setSearch] = useState('');

  const { members, loading, error, refetch } = useOrganizationAcceptedMembers(organizationUserId);

  useEffect(() => {
    if (!refreshSignal) return;
    refetch();
  }, [refreshSignal, refetch]);

  const filteredMembers = useMemo(() => filterMembersBySearch(members, search), [members, search]);

  return (
    <Box
      as="section"
      mt="4"
      bg="white"
      border="1px solid"
      borderColor="volyx.mediumGray"
      borderRadius="24px"
      px="5"
      py="4"
      boxShadow="0 2px 12px rgba(0,0,0,0.06)"
    >
      <Flex align="center" gap="3" mb="4">
        <Box
          w="44px"
          h="44px"
          borderRadius="full"
          bg="volyx.primaryLight"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Users size={20} color="#007AFF" aria-hidden />
        </Box>
        <Box minW="0" flex="1">
          <Text m="0" fontSize="sm" fontWeight="bold" color="volyx.text" lineClamp={1}>
            Members
          </Text>
          <Text m="0" mt="0.5" fontSize="xs" color="volyx.textSecondary">
            Volunteers who are approved to join your organization.
          </Text>
        </Box>
      </Flex>

      {loading && (
        <Flex justify="center" py="6">
          <Spinner color="volyx.primary" size="md" />
        </Flex>
      )}

      {!loading && error && (
        <Text fontSize="sm" color="red.500" m="0">
          {error}
        </Text>
      )}

      {!loading && !error && members.length === 0 && (
        <Text fontSize="sm" color="volyx.textSecondary" m="0" py="2">
          No members yet. Accept requests from the Requests tab to add people here.
        </Text>
      )}

      {!loading && !error && members.length > 0 && (
        <>
          <Flex
            align="center"
            gap="3"
            mb="3"
            bg="rgba(255,255,255,0.95)"
            border="1px solid"
            borderColor="volyx.mediumGray"
            borderRadius="xl"
            px="3"
            minH="12"
            boxShadow="0 1px 3px rgba(0,0,0,0.06)"
          >
            <Box as="span" color="volyx.darkGray" flexShrink={0} display="inline-flex" aria-hidden>
              <Search size={20} />
            </Box>
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search members by name..."
              flex="1"
              border="none"
              bg="transparent"
              fontSize="sm"
              color="volyx.text"
              outline="none"
              minW="0"
              px="0"
              _placeholder={{ color: 'volyx.darkGray' }}
              aria-label="Search members"
            />
          </Flex>

          {filteredMembers.length === 0 ? (
            <Text fontSize="sm" color="volyx.textSecondary" m="0" py="2">
              No members match &quot;{search.trim()}&quot;.
            </Text>
          ) : (
            <Box
              maxH={MEMBERS_LIST_MAX_HEIGHT}
              overflowY="auto"
              overflowX="hidden"
              pr="1"
              css={{
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <Flex direction="column" gap="3">
                {filteredMembers.map((m, index) => {
                  const rowLoading = loadingMembershipId === m.membershipId;
                  return (
                  <Flex
                    key={m.membershipId}
                    align="center"
                    gap="3"
                    borderTop={index === 0 ? undefined : '1px solid'}
                    borderColor="gray.100"
                    pt={index === 0 ? 0 : 3}
                  >
                    <Image
                      src={m.profileImage || DEFAULT_PROFILE_IMAGE}
                      alt=""
                      boxSize="44px"
                      borderRadius="full"
                      objectFit="cover"
                      flexShrink={0}
                      border="1px solid"
                      borderColor="volyx.mediumGray"
                    />
                    <Box minW="0" flex="1">
                      <Flex align="center" gap="2" minW="0">
                        <Text m="0" fontSize="sm" fontWeight="semibold" color="volyx.text" lineClamp={1}>
                          {displayName(m)}
                        </Text>
                        {m.role === 'admin' && (
                          <Badge
                            px="2"
                            py="0.5"
                            borderRadius="full"
                            bg="volyx.primaryLight"
                            borderWidth="1.5px"
                            borderColor="#007AFF"
                            fontSize="10px"
                            fontWeight="semibold"
                            color="#007AFF"
                            flexShrink={0}
                          >
                            Admin
                          </Badge>
                        )}
                      </Flex>
                      <Text m="0" fontSize="xs" color="volyx.textSecondary">
                        Joined{' '}
                        {new Date(m.joinedAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </Text>
                    </Box>
                    <Button
                      type="button"
                      w="40px"
                      h="40px"
                      minW="40px"
                      p="0"
                      borderRadius="full"
                      bg="volyx.primary"
                      flexShrink={0}
                      transition="all 0.2s"
                      _hover={{ opacity: 0.85, transform: 'scale(1.05)' }}
                      aria-label="Manage member"
                      aria-busy={rowLoading}
                      disabled={rowLoading}
                      onClick={() => onManageMember(m)}
                    >
                      {rowLoading ? (
                        <Spinner size="sm" color="white" />
                      ) : (
                        <UserCog size={18} color="white" aria-hidden />
                      )}
                    </Button>
                  </Flex>
                  );
                })}
              </Flex>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
